/**
 * TripMemoriesSection — lightweight preview of photos already added to
 * the trip + CTAs to open the full gallery / scrapbook flows. Uses the
 * existing photos endpoint so no new backend work is required.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Camera, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, BACKEND_URL } from "@/config/api";

export default function TripMemoriesSection({ trip }) {
  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/items/${trip.id}/photos`);
        if (!cancelled) setPhotos(res.data || []);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [trip.id]);

  return (
    <div className="rounded-2xl bg-card border border-border p-5" data-testid="trip-memories-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Memories</p>
          <p className="font-serif text-xl text-foreground">{photos.length} photo{photos.length === 1 ? "" : "s"}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.dispatchEvent(new CustomEvent("duolyst:open-photos", { detail: { itemId: trip.id } }))}
          className="rounded-full"
        >
          <Camera className="w-4 h-4 mr-1.5" /> Add
        </Button>
      </div>
      {photos.length > 0 && (
        <div className="grid grid-cols-4 gap-1.5">
          {photos.slice(0, 8).map((p) => (
            <img
              key={p.id}
              src={`${BACKEND_URL}/api/photos/${p.id}/file`}
              alt=""
              loading="lazy"
              className="aspect-square w-full object-cover rounded-md"
              onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
            />
          ))}
        </div>
      )}
      {photos.length === 0 && (
        <p className="text-sm text-muted-foreground">No photos yet. They&apos;ll appear here as you add them.</p>
      )}
    </div>
  );
}
